import { DOCUMENT } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AfterRenderRef, AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, Inject, Injector, OnDestroy, OnInit, ViewEncapsulation, afterNextRender } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { filter, map, shareReplay, startWith, takeUntil } from 'rxjs/operators';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';

import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';

import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';

import { DataService } from '@app/core/services/data.service';
import { ImageService } from '@app/shared/services/image.service';
import { BadisOauthLoginService } from '@app/core/services/auth/badis-oauth-login.service';
import { UserAccessToken } from '@app/core/services/auth/model/auth-user.interface';
import { NetworkStatusService } from '@app/shared/services/network-status.service';
import { NotificationService } from '@app/pages/notifications/services/notification.service';
import { BackgroundService } from '@app/shared/components/background/background.service';
import { GlobalVariablesService } from '@app/core/services/global-variables.service';
import { ScreenSaverService } from '@app/core/services/screen-saver.service';
import { ChatStateService } from '@app/core/services/chat/chat-state.service';
import { ChatUiService } from '@app/core/services/chat/chat-ui.service';
import { UpdateCheckService } from '@app/shared/services/update-check.service';
import { CommandCenterShellService } from '@app/core/services/command-center-shell.service';
import { SettingsService } from '@app/core/services/settings/settings.service';
import { appConfig } from 'app/core/config/app.config';
import { ToastNotifyService } from '@app/pages/notifications/services/toast-notify.service';

type NotificationMuteMode = '15m' | '1h' | 'today';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    styleUrls: ['./classy.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ClassyLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
    isScreenSmall = false;
    navigationMode: 'over' | 'side' = 'side';
    navigationOpened = true;
    navigation: Navigation;
    public user: UserAccessToken;
    public isCommandCenterMode = false;
    public hideShellForFullscreenRoute = false;

    public displayName: string;
    public userName: string;
    public avatar: any;
    public userId: string;

    private _unsubscribeAll: Subject<any> = new Subject<any>();


    notificationData$!: ReturnType<NotificationService['getNotificationsData']>;
    chatUnreadCount$!: Observable<number>;
    toastMuted$!: Observable<boolean>;
    toastMuteCountdownLabel$!: Observable<string | null>;
    toastMuteRemainingMinutes$!: Observable<number | null>;
    activeToastCount$!: Observable<number>;
    queuedMessageCount$!: Observable<number>;
    notificationBadgeCount$!: Observable<number>;
    chatConversationId: string | null = null;
    isChatOpen = false;
    isChatEnabled = false;
    updatePending$!: Observable<boolean>;
    selectedNotificationMuteMode: NotificationMuteMode | null = null;
    readonly notificationMuteOptions = [
        { labelKey: 'SystemAdmin.Notifications.Mute15Minutes', seconds: 15 * 60, mode: '15m' as const },
        { labelKey: 'SystemAdmin.Notifications.MuteFor1Hour', seconds: 60 * 60, mode: '1h' as const }
    ];
    private pendingNotificationMuteMode: NotificationMuteMode | null = null;
    private notificationMuteSelectedUntilUtc: string | null = null;
    private _commandCenterNavigationVisible = true;
    private _commandCenterPreviousNavigationOpened: boolean | null = null;
    private _viewInitialized = false;
    private _commandCenterNavigationRenderRef?: AfterRenderRef;
    private _fullscreenNavigationRenderRef?: AfterRenderRef;
    private _responsiveNavigationTimer?: ReturnType<typeof setTimeout>;
    private readonly _navigationMdQuery = `(min-width: ${appConfig.screens.md})`;
 
    constructor(
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _badisOauthLoginService: BadisOauthLoginService,
        private dataService: DataService,
        private imageService: ImageService,
        public networkStatusService: NetworkStatusService,
        private notificationService: NotificationService,
        public toastNotify: ToastNotifyService,
        private chatState: ChatStateService,
        private chatUi: ChatUiService,
        private settingsService: SettingsService,
        private updateCheckService: UpdateCheckService,
        private commandCenterShell: CommandCenterShellService,
        private breakpointObserver: BreakpointObserver,
        private router: Router,
        private injector: Injector,
        private destroyRef: DestroyRef,
        @Inject(DOCUMENT) private document: Document,
        public backgroundService: BackgroundService,
        public globals: GlobalVariablesService,
        public screenSaver: ScreenSaverService
    ) {
        this.applyResponsiveNavigationState(this.breakpointObserver.isMatched(this._navigationMdQuery));

        this._badisOauthLoginService.onAuthStatusChanged.subscribe((isAuthenticated: boolean) => {

            if (isAuthenticated) {

                const authUser = this._badisOauthLoginService.userAccessToken;
                this.displayName = authUser ? authUser.displayName : 'Navizard';
                this.avatar = authUser.avatar;
                this.userName = authUser.userName;
                this.userId = authUser.userId;
            }

        });
    }

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    get showShellHeader(): boolean {
        return !this.isCommandCenterMode || this._commandCenterNavigationVisible;
    }

    ngOnInit(): void {
        this.notificationData$ = this.notificationService.getNotificationsData();
        this.chatUnreadCount$ = this.chatState.unreadCount$;
        this.toastMuted$ = this.notificationService.getMuteState().pipe(
            map((state) => !!state?.isMuted),
            shareReplay({ bufferSize: 1, refCount: true })
        );
        this.notificationService.getMuteState()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((state) => this.syncNotificationMuteSelection(state));

        const toastMuteCountdown$ = this.notificationService.getMuteRemainingSeconds().pipe(
            shareReplay({ bufferSize: 1, refCount: true })
        );
        this.toastMuteCountdownLabel$ = toastMuteCountdown$.pipe(
            map((seconds) => {
                if (!Number.isFinite(seconds) || seconds <= 0) {
                    return null;
                }

                const remainingMinutes = Math.max(1, Math.ceil(seconds / 60));
                return `${remainingMinutes}m`;
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );
        this.toastMuteRemainingMinutes$ = toastMuteCountdown$.pipe(
            map((seconds) => {
                if (!Number.isFinite(seconds) || seconds <= 0) {
                    return null;
                }

                return Math.max(1, Math.ceil(seconds / 60));
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );
        this.activeToastCount$ = this.toastNotify.getActiveNotifications().pipe(
            map((count) => Number.isFinite(count) ? Math.max(0, count) : 0),
            startWith(this.toastNotify.activeNotifications ?? 0),
            shareReplay({ bufferSize: 1, refCount: true })
        );
        this.queuedMessageCount$ = this.notificationData$.pipe(
            map((data) => Math.max(0, data?.unreadMessages ?? 0)),
            shareReplay({ bufferSize: 1, refCount: true })
        );
        this.notificationBadgeCount$ = this.notificationData$.pipe(
            map((data) => Math.max(0, data?.unreadMessages ?? 0)),
            shareReplay({ bufferSize: 1, refCount: true })
        );
        this.updatePending$ = this.updateCheckService.updateDeferred$;
        this.isChatEnabled = this.resolveChatEnabled();

        this.chatUi.open$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((request) => {
                if (!this.isChatEnabled || !this.showShellHeader) {
                    return;
                }

                this.chatConversationId = request?.conversationId ?? null;
                this.isChatOpen = true;
            });

        this.settingsService.settingChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((setting) => {
                if (setting?.primaryKey !== 'Chat.Enabled') {
                    return;
                }

                this.isChatEnabled = !!setting.userSetting?.getValue<boolean>();
                if (!this.isChatEnabled) {
                    this.closeChat();
                }
            });
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        // this._userService.user$
        //     .pipe(takeUntil(this._unsubscribeAll))
        //     .subscribe((user: User) => {
        //         this.user = user;
        //     });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                this.scheduleResponsiveNavigationState(matchingAliases.includes('md'));
            });

        this.commandCenterShell.state$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((state) => {
                this.isCommandCenterMode = state.active;
                this._commandCenterNavigationVisible = state.navigationVisible;
                if (!this.showShellHeader) {
                    this.closeChat();
                }
                this.syncCommandCenterNavigation();
            });

        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => this.syncFullscreenShellVisibility());

        this.document.addEventListener('fullscreenchange', this.onFullscreenChanged);
        this.syncFullscreenShellVisibility();
    }

    ngAfterViewInit(): void {
        this._viewInitialized = true;
        this.syncCommandCenterNavigation();
    }

    ngOnDestroy(): void {
        this.document.removeEventListener('fullscreenchange', this.onFullscreenChanged);
        this.document.body.classList.remove('reader-calibration-fullscreen');
        this._commandCenterNavigationRenderRef?.destroy();
        this._commandCenterNavigationRenderRef = undefined;
        this._fullscreenNavigationRenderRef?.destroy();
        this._fullscreenNavigationRenderRef = undefined;
        if (this._responsiveNavigationTimer) {
            clearTimeout(this._responsiveNavigationTimer);
            this._responsiveNavigationTimer = undefined;
        }
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    toggleChat(): void {
        if (!this.isChatEnabled) {
            this.closeChat();
            return;
        }

        this.isChatOpen = !this.isChatOpen;
    }

    closeChat(): void {
        this.isChatOpen = false;
        this.chatConversationId = null;
    }

    applyPendingUpdate(): void {
        this.updateCheckService.resumeDeferredUpdate();
    }

    muteNotificationsFor(seconds: number): void {
        this.pendingNotificationMuteMode = seconds === 15 * 60 ? '15m' : '1h';
        this.notificationService.muteForSeconds(seconds).subscribe();
    }

    muteNotificationsToday(): void {
        const now = new Date();
        const endOfDay = new Date(now);
        endOfDay.setHours(23, 59, 59, 999);
        const seconds = Math.max(60, Math.ceil((endOfDay.getTime() - now.getTime()) / 1000));
        this.pendingNotificationMuteMode = 'today';
        this.notificationService.muteForSeconds(seconds).subscribe();
    }

    unmuteNotifications(): void {
        this.pendingNotificationMuteMode = null;
        this.selectedNotificationMuteMode = null;
        this.notificationMuteSelectedUntilUtc = null;
        this.notificationService.unmute().subscribe();
    }

    clearToastNotifications(): void {
        this.notificationService.clearMessages();
        this.toastNotify.clearNotifications();
    }

    openNotifications(): void {
        void this.router.navigate(['/notifications']);
    }

    private syncNotificationMuteSelection(state: { isMuted: boolean; mutedUntilUtc?: string | null } | null): void {
        const mutedUntilUtc = state?.mutedUntilUtc ?? null;

        if (!state?.isMuted || !mutedUntilUtc) {
            this.pendingNotificationMuteMode = null;
            this.selectedNotificationMuteMode = null;
            this.notificationMuteSelectedUntilUtc = null;
            return;
        }

        if (this.pendingNotificationMuteMode) {
            this.selectedNotificationMuteMode = this.pendingNotificationMuteMode;
            this.pendingNotificationMuteMode = null;
            this.notificationMuteSelectedUntilUtc = mutedUntilUtc;
            return;
        }

        if (this.notificationMuteSelectedUntilUtc === mutedUntilUtc && this.selectedNotificationMuteMode) {
            return;
        }

        this.selectedNotificationMuteMode = this.inferNotificationMuteMode(mutedUntilUtc);
        this.notificationMuteSelectedUntilUtc = mutedUntilUtc;
    }

    private inferNotificationMuteMode(mutedUntilUtc: string): NotificationMuteMode {
        const remainingSeconds = Math.ceil((new Date(mutedUntilUtc).getTime() - Date.now()) / 1000);

        if (remainingSeconds > 60 * 60) {
            return 'today';
        }

        if (remainingSeconds > 15 * 60) {
            return '1h';
        }

        return '15m';
    }

    public loadActiveTagsFilter(): void {

    }

    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }

    }

    private applyResponsiveNavigationState(isMdMatched: boolean): void {
        const nextIsScreenSmall = !isMdMatched;
        const nextMode: 'over' | 'side' = nextIsScreenSmall ? 'over' : 'side';
        const nextOpened = !nextIsScreenSmall;

        if (
            this.isScreenSmall === nextIsScreenSmall &&
            this.navigationMode === nextMode &&
            this.navigationOpened === nextOpened
        ) {
            return;
        }

        this.isScreenSmall = nextIsScreenSmall;
        this.navigationMode = nextMode;
        this.navigationOpened = nextOpened;
        this.syncCommandCenterNavigation();
    }

    private scheduleResponsiveNavigationState(isMdMatched: boolean): void {
        if (this._responsiveNavigationTimer) {
            clearTimeout(this._responsiveNavigationTimer);
        }

        this._responsiveNavigationTimer = setTimeout(() => {
            this._responsiveNavigationTimer = undefined;
            if (this.destroyRef.destroyed) {
                return;
            }

            this.applyResponsiveNavigationState(isMdMatched);
        });
    }

    private syncCommandCenterNavigation(): void {
        if (!this._viewInitialized) {
            return;
        }

        this._commandCenterNavigationRenderRef?.destroy();
        this._commandCenterNavigationRenderRef = afterNextRender(() => {
            this._commandCenterNavigationRenderRef = undefined;
            if (this.destroyRef.destroyed) {
                return;
            }

            const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
            if (!navigation) {
                return;
            }

            if (this.isCommandCenterMode) {
                if (this._commandCenterPreviousNavigationOpened === null) {
                    this._commandCenterPreviousNavigationOpened = navigation.opened;
                }

                if (this._commandCenterNavigationVisible) {
                    navigation.open();
                } else {
                    navigation.close();
                }

                return;
            }

            if (this._commandCenterPreviousNavigationOpened !== null) {
                if (this._commandCenterPreviousNavigationOpened) {
                    navigation.open();
                } else {
                    navigation.close();
                }

                this._commandCenterPreviousNavigationOpened = null;
            }
        }, { injector: this.injector });
    }

    private readonly onFullscreenChanged = (): void => {
        this.syncFullscreenShellVisibility();
    };

    private syncFullscreenShellVisibility(): void {
        const isReaderCalibrationRoute = this.router.url.split('?')[0].startsWith('/reader-calibration');
        const shouldHide = isReaderCalibrationRoute && !!this.document.fullscreenElement;

        this.document.body.classList.toggle('reader-calibration-fullscreen', shouldHide);

        if (this.hideShellForFullscreenRoute === shouldHide) {
            return;
        }

        this.hideShellForFullscreenRoute = shouldHide;

        if (shouldHide) {
            this.closeChat();
            this._fullscreenNavigationRenderRef?.destroy();
            this._fullscreenNavigationRenderRef = afterNextRender(() => {
                this._fullscreenNavigationRenderRef = undefined;
                if (this.destroyRef.destroyed) {
                    return;
                }

                this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation')?.close();
            }, { injector: this.injector });
        }
    }

    public avatarLoadError = false;

    public photoLink(photoId: string): any {
        // Use ImageService with userId for photo microservice URL
        if (this.userId) {
            try {
                return this.imageService.getImageUrl(this.userId as any, 0.8, this.userId as any, 256);
            } catch (error) {
                this.avatarLoadError = true;
                return this.dataService.getAvatarPhoto(photoId);
            }
        }
        return this.dataService.getAvatarPhoto(photoId);
    }

    public errorHandler(event: any): any {
        this.avatarLoadError = true;
    }

    public backErrorHandler(event: any): any {
        event.target.src = './assets/images/logo/navizard.png';
    }

    private resolveChatEnabled(): boolean {
        return !!this.settingsService.getSettingValue<boolean>('Chat.Enabled');
    }


}
